import { useDemoGuard } from '@hooks';
import { NotificationSubscription } from '@models';
import { DocumentService, InventoryService, ParsedInvoice, PurchaseOrderMatchResult, PurchaseOrderService, SupplierLookup } from '@services';
import { useStore } from '@stores';
import { message } from 'antd';
import { observer } from 'mobx-react';
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { POInvoiceReceiveDrawerPresentation } from './POInvoiceReceiveDrawer.presentation';

declare const abp: any;

export interface POInvoiceReceiveDrawerHandle {
  open: () => void;
}

interface IProps {
  onSaved: () => void;
}

export const POInvoiceReceiveDrawer = observer(
  forwardRef<POInvoiceReceiveDrawerHandle, IProps>(({ onSaved }, ref) => {
    const { purchaseOrderStore } = useStore();
    const { guard } = useDemoGuard();

    const [open, setOpen] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<{ base64: string; name: string; contentType: string } | null>(null);
    const [captureToken, setCaptureToken] = useState<string | null>(null);
    const captureTokenRef = useRef<string | null>(null);
    captureTokenRef.current = captureToken;

    const [parsing, setParsing] = useState(false);
    const [matching, setMatching] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [parsedInvoice, setParsedInvoice] = useState<ParsedInvoice | null>(null);
    const [supplierId, setSupplierId] = useState<string | undefined>();
    const [attachedSupplier, setAttachedSupplier] = useState<SupplierLookup | undefined>();
    const [matchResults, setMatchResults] = useState<PurchaseOrderMatchResult[]>([]);

    // Per-line editable state: index → value
    const [lineQtys, setLineQtys] = useState<Record<number, number>>({});
    const [lineCosts, setLineCosts] = useState<Record<number, number | undefined>>({});
    const [lineSelectedPO, setLineSelectedPO] = useState<Record<number, string | undefined>>({});

    // QR mobile capture via SignalR notification
    useEffect(() => {
      const handler = async (userNotification: any) => {
        const name = userNotification?.notification?.notificationName;
        if (name !== NotificationSubscription.InventoryCaptureCompleted) return;
        const data = userNotification?.notification?.data?.properties;
        if (!data || data.token !== captureTokenRef.current) return;
        try {
          const file = await DocumentService.get({ id: data.documentId });
          const contentType = file.contentType || 'image/jpeg';
          setUploadedFile({
            base64: `data:${contentType};base64,${file.base64Content}`,
            name: file.name || 'Mobile capture',
            contentType,
          });
          message.success('Invoice image received from mobile!');
        } catch {
          message.warning('Invoice received but preview could not be loaded.');
        }
      };
      abp.event.on('abp.notifications.received', handler);
      return () => abp.event.off('abp.notifications.received', handler);
    }, []);

    const reset = useCallback(() => {
      setOpen(false);
      setUploadedFile(null);
      setCaptureToken(null);
      setParsedInvoice(null);
      setSupplierId(undefined);
      setAttachedSupplier(undefined);
      setMatchResults([]);
      setLineQtys({});
      setLineCosts({});
      setLineSelectedPO({});
    }, []);

    useImperativeHandle(ref, () => ({
      open: async () => {
        reset();
        setOpen(true);
        try {
          const session = await InventoryService.createCaptureSession();
          setCaptureToken(session.token);
        } catch {
          console.error('Failed to create capture session');
        }
      },
    }));

    const handleFileSelect = useCallback((file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setUploadedFile({
          base64: e.target?.result as string,
          name: file.name,
          contentType: file.type,
        });
      };
      reader.readAsDataURL(file);
    }, []);

    const handleParse = guard(async () => {
      if (!uploadedFile) return;
      setParsing(true);
      try {
        const result = await PurchaseOrderService.parseInvoice({
          name: uploadedFile.name,
          content: uploadedFile.base64,
          contentType: uploadedFile.contentType,
        });
        setParsedInvoice(result);
        message.success(`Extracted ${result.lineItems?.length ?? 0} lines from invoice`);
      } catch (err) {
        message.error('Failed to parse invoice');
        console.error(err);
      } finally {
        setParsing(false);
      }
    });

    const handleAttachSupplier = useCallback(async () => {
      if (!parsedInvoice?.supplierName) return;
      try {
        const supplier = await InventoryService.findOrCreateSupplier({
          name: parsedInvoice.supplierName,
          email: parsedInvoice.supplierEmail || undefined,
          phone: parsedInvoice.supplierPhone || undefined,
          address: parsedInvoice.supplierAddress || undefined,
        });
        setAttachedSupplier({ id: supplier.id, name: supplier.name });
        setSupplierId(supplier.id);
        message.success('Supplier attached');
      } catch {
        message.error('Failed to attach supplier');
      }
    }, [parsedInvoice]);

    const handleMatch = useCallback(async () => {
      if (!supplierId || !parsedInvoice?.lineItems?.length) return;
      setMatching(true);
      try {
        await purchaseOrderStore.matchInvoice(supplierId, parsedInvoice.lineItems);
        const results = purchaseOrderStore.matchResults;
        setMatchResults(results);
        // Seed per-line qtys from suggested values
        const qtys: Record<number, number> = {};
        results.forEach((r, i) => {
          qtys[i] = r.suggestedReceivedQty ?? r.parsedLine.quantity ?? 1;
        });
        setLineQtys(qtys);
      } catch {
        message.error('Failed to match invoice lines');
      } finally {
        setMatching(false);
      }
    }, [supplierId, parsedInvoice, purchaseOrderStore]);

    const handleSubmit = useCallback(async () => {
      if (!supplierId || submitting) return;
      setSubmitting(true);
      try {
        const lines = matchResults
          .map((result, index) => {
            if (result.matchStatus === 'NoMatch') return null;
            const poId = lineSelectedPO[index] ?? result.bestMatch?.id;
            if (!poId) return null;
            return {
              purchaseOrderId: poId,
              receivedQuantity: lineQtys[index] ?? result.suggestedReceivedQty ?? 1,
              costPrice: lineCosts[index],
            };
          })
          .filter((l): l is NonNullable<typeof l> => l !== null);

        await purchaseOrderStore.bulkReceive({
          supplierId,
          supplierInvoiceNumber: parsedInvoice?.invoiceNumber || undefined,
          lines,
        });
        reset();
        onSaved();
      } catch {
        message.error('Failed to bulk receive');
      } finally {
        setSubmitting(false);
      }
    }, [supplierId, submitting, matchResults, lineQtys, lineCosts, lineSelectedPO, parsedInvoice, purchaseOrderStore, reset, onSaved]);

    return (
      <POInvoiceReceiveDrawerPresentation
        open={open}
        uploadedFile={uploadedFile}
        parsing={parsing}
        matching={matching}
        submitting={submitting}
        captureToken={captureToken}
        parsedInvoice={parsedInvoice}
        supplierId={supplierId}
        attachedSupplier={attachedSupplier}
        matchResults={matchResults}
        lineQtys={lineQtys}
        lineCosts={lineCosts}
        lineSelectedPO={lineSelectedPO}
        onClose={reset}
        onFileSelect={handleFileSelect}
        onRemoveFile={() => setUploadedFile(null)}
        onParse={handleParse}
        onAttachSupplier={handleAttachSupplier}
        onSupplierChange={setSupplierId}
        onMatch={handleMatch}
        onLineQtyChange={(i, val) => setLineQtys((prev) => ({ ...prev, [i]: val }))}
        onLineCostChange={(i, val) => setLineCosts((prev) => ({ ...prev, [i]: val }))}
        onLineSelectPO={(i, id) => setLineSelectedPO((prev) => ({ ...prev, [i]: id }))}
        onSubmit={handleSubmit}
      />
    );
  }),
);
